import React, {useState, useMemo} from "react";
import {Column, Row, Text, Spinner} from "react-vcomponents";
import {ShowMessageBox} from "react-vmessagebox";
import {observer_mgl} from "mobx-graphlink";
import {DialogStyle} from "../../../../../Utils/UI/GlobalStyles.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {ObjPath} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {CollectMaterialContextRelevantObjects, CollectSceneryRelevantObjects, CollectTerrainRelevantObjects, PathWorldCentroid} from "../../../../../Terrain/Heights/TerrainExporter.js";
import {ToJSON_Compact} from "../../../../../Utils/General/Serialization.js";
import {Rect, Vec2} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {ResolveMapObjectsForTerrain} from "../../../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Resolver.js";
import {AugmentTerrainObjectsWithMaterialData, AugmentTerrainObjectsWithSceneryData} from "../../../../../Terrain/Heights/Poisson/@Shared/ConstraintRasterizer/MapObject_ForTerrain_Augments.js";
import {GetCameraViewJSON, ObjExportJSONFixturePanel} from "./ObjExportDialog_Base.js";

export type AreaExportKind = "terrain" | "material" | "scenery";

/** The exported domain's descriptive labels — used in the dialog's object-count line + the
 *  copy help-text. Shared between the terrain + material + scenery variants so they stay in sync. */
const KindInfo: Record<AreaExportKind, {label: string; title: string; help: string}> = {
	terrain: {
		label: "terrain-relevant",
		title: "Export area for unit-tests",
		help: "Paste this JSON as the fixture in a new test case. The `objects` array is the terrain-objects (resolved via `ResolveMapObjectsForTerrain`, so paths carry `_meshSet_world` + `_curve_closed_resolved`); the `cameraView` field is the current camera view-rect (minX/minY/maxX/maxY in world coords) — use it as the test's `cameraView` for exact zoom-level parity. Ask an LLM to write the assertions based on your description of the observed issue.",
	},
	material: {
		label: "material-context",
		title: "Export area for unit-tests (Material)",
		help: "Paste this JSON as the fixture in a new test case. The `objects` array is the material-context objects (terrain-objects — paths carry `materialFeature` + `_meshSet_world` + `_curve_closed_resolved`; the `Mod_TerrainMaterials` modifier is re-attached) — feed it to `collectMaterialRegions` for splat/material tests; the `cameraView` field is the current camera view-rect (minX/minY/maxX/maxY in world coords). Ask an LLM to write the assertions based on your description of the observed issue.",
	},
	scenery: {
		label: "scenery-relevant",
		title: "Export area for unit-tests (Scenery)",
		help: "Paste this JSON as the fixture in a new test case. The `objects` array is the scenery-context objects (terrain-objects — paths carry `sceneryFeature` + `_meshSet_world` + `_curve_closed_resolved`; the `Mod_TerrainSceneries` modifier is re-attached) — feed it to `collectSceneryRegions` + `placeSceneryTile` for placement tests; the `cameraView` field is the current camera view-rect (minX/minY/maxX/maxY in world coords). Ask an LLM to write the assertions based on your description of the observed issue.",
	},
};

export function ShowTerrainExportDialog(obj: MapObject, path: ObjPath, allMapObjects: MapObject[], mapID: string) {
	ShowAreaExportDialog("terrain", obj, path, allMapObjects, mapID);
}

export function ShowMaterialExportDialog(obj: MapObject, path: ObjPath, allMapObjects: MapObject[], mapID: string) {
	ShowAreaExportDialog("material", obj, path, allMapObjects, mapID);
}

export function ShowSceneryExportDialog(obj: MapObject, path: ObjPath, allMapObjects: MapObject[], mapID: string) {
	ShowAreaExportDialog("scenery", obj, path, allMapObjects, mapID);
}

function ShowAreaExportDialog(kind: AreaExportKind, obj: MapObject, path: ObjPath, allMapObjects: MapObject[], mapID: string) {
	const info = KindInfo[kind];
	ShowMessageBox({
		title: info.title,
		cancelButton: true,
		okButton: false,
		message: ()=><AreaExportDialogUI kind={kind} obj={obj} path={path} allMapObjects={allMapObjects} mapID={mapID}/>,
	});
}

const AreaExportDialogUI = observer_mgl(({kind, obj, path, allMapObjects, mapID}: {
	kind: AreaExportKind;
	obj: MapObject;
	path: ObjPath;
	allMapObjects: MapObject[];
	mapID: string;
})=>{
	const [sideLength, setSideLength] = useState(5000);
	const [includeCamView, setIncludeCamView] = useState(true);

	const exportHalfSide = sideLength / 2;

	const {objects, reasons} = useMemo(()=>{
		const center = PathWorldCentroid(path, obj);
		const inclusionRect = Rect.FromCenter(new Vec2(center.x, center.y), Vec2.one.Times(2 * exportHalfSide));
		return (
			kind == "terrain" ? CollectTerrainRelevantObjects(allMapObjects, {inclusionRect}) :
			kind == "material" ? CollectMaterialContextRelevantObjects(allMapObjects, {inclusionRect}) :
			CollectSceneryRelevantObjects(allMapObjects, {inclusionRect})
		);
	}, [kind, obj, path, exportHalfSide, allMapObjects]);

	const terrainObjects = ResolveMapObjectsForTerrain(objects,
		/*resolvePathOriginPathIDs:*/ true,
		/*keepPathMeshLocalPoints:*/ false,
		/*calcPathMeshWorldPoints:*/ true,
	);
	// Re-attach the map-wide modifier data the terrain whitelist drops (it can affect the
	// material/scenery pipeline outputs). Terrain kind doesn't need this (already whitelisted).
	const augmentedObjects = kind == "material"
		? AugmentTerrainObjectsWithMaterialData(terrainObjects, objects)
		: kind == "scenery"
			? AugmentTerrainObjectsWithSceneryData(terrainObjects, objects)
			: terrainObjects;
	// Serialize: a wrapper object containing both the terrain-objects array and the camera
	// view-rect. The old format (a bare array) is preserved for backward compat — the wrapper
	// has a `_cameraView` field that tests can read, and a `objects` field with the array.
	const cameraViewJSON = GetCameraViewJSON(mapID, includeCamView);
	const json = cameraViewJSON
		? ToJSON_Compact({cameraView: cameraViewJSON, objects: augmentedObjects})
		: ToJSON_Compact(augmentedObjects);

	return (
		<Column style={DialogStyle({width: 700})}>
			<Row center>
				<Text mr={5}>Export side length (m):</Text>
				<Spinner min={1} step={50} value={sideLength} onChange={val=>setSideLength(val)}/>
				<Text ml={10} style={{opacity: 0.7, fontSize: 12}}>
					(±{exportHalfSide}m square from path centroid)
				</Text>
			</Row>
			<Row mt={5}>
				<Text style={{fontWeight: "bold"}}>
					Found {objects.length} {KindInfo[kind].label} object{objects.length !== 1 ? "s" : ""}:
				</Text>
			</Row>
			<Column mt={5} style={{
				maxHeight: 160,
				overflowY: "auto",
				background: "rgba(0,0,0,0.2)",
				borderRadius: 4,
				padding: "4px 8px",
			}}>
				{objects.length === 0 && (
					<Text style={{opacity: 0.6, fontStyle: "italic"}}>No objects found in this area.</Text>
				)}
				{objects.map(o=>(
					<Row key={o.id} style={{fontSize: 12, padding: "1px 0"}}>
						<Text style={{minWidth: 120, fontFamily: "monospace", opacity: 0.7}}>{o.id.slice(0, 8)}…</Text>
						<Text mr={5} style={{fontWeight: "bold"}}>{o.name}</Text>
						<Text style={{opacity: 0.6}}>— {reasons.get(o.id)?.join("; ")}</Text>
					</Row>
				))}
			</Column>

			<ObjExportJSONFixturePanel json={json} includeCamView={includeCamView} setIncludeCamView={setIncludeCamView}
				helpText={KindInfo[kind].help}/>
		</Column>
	);
});